import Button from 'Nori/Button';
import Hero from 'Nori/Hero/HeroFull2';
import Image from 'Nori/Image';
import Layout from 'Nori/Layout';
import { useEffect, useRef, useState } from 'react';
// import Hero from 'Nori/Hero';
import CardsBlock from 'Custom/CardsBlock';
import EnrollmentCards from 'Custom/EnrollmentCards';
import { useRouter } from 'next/router';
import { useAppContext } from 'Providers/App';
import { UI_ELEMENTS } from 'Providers/App/elements';
import styles from 'styles/pages/enrollment.module.scss';
import {
  createHandleOpenLogin,
  PATIENT_HUB_CONFIG,
  useGigyaReady,
  useGigyaSessionVerify,
} from 'Utils/hooks/useGigyaService';

import CITY_IMAGE_MOBILE from 'Images/people/enrollment-hero-mobile.webp';
// import HAND_CARD from 'Images/misc/hand-card.webp';
import CALENDAR_CARD from 'Images/misc/CALENDAR_CARD.webp';
import LADY_CARD from 'Images/misc/ladycard.png';
import PHONE_CARD from 'Images/misc/PHONE_CARD.webp';

import HERO_BG_IMAGE from 'Images/background/pink-bg.png';
import HERO_BG_IMAGE_MOBILE from 'Images/background/pink-mobile-banner.png';
import Container from 'Nori/Container/index';

const HEAD = {
  title: 'Enroll for NEXPLANON® (etonogestrel implant) 68 mg Radiopaque',
  description:
    'Register to receive helpful tips and support throughout your NEXPLANON® (etonogestrel implant) 68 mg Radiopaque journey.',
  keywords: 'Nexplanon Enrollment, Register for Nexplanon',
};

const HERO_IMAGE = {
  src: HERO_BG_IMAGE,
  fill: true,
  objectFit: 'cover',
  objectPosition: '70% top',
};

const HERO_MOBILE_IMAGE = {
  src: CITY_IMAGE_MOBILE,
  fill: true,
  objectFit: 'cover',
  objectPosition: 'center center',
};

const ENROLLMENT_CARDS = [
  {
    image: <Image alt="" src={LADY_CARD} />,
    children: (
      <h3>
        Track your insertion and removal dates on the My NEXPLANON dashboard
      </h3>
    ),
  },
  {
    image: <Image alt="" src={PHONE_CARD} />,
    children: (
      <h3>
        Receive tips and guides to help you along your journey with NEXPLANON
      </h3>
    ),
  },
  {
    image: <Image alt="" src={CALENDAR_CARD} />,
    children: (
      <h3>
        Receive reminders on when it&#39;s time to have another NEXPLANON
        inserted, if desired
      </h3>
    ),
  },
];

const ENROLLMENT_SCREEN = {
  screenSet: 'NexplanonPatientPortalHub',
  startScreen: 'gigya-reg-screen',
  containerID: 'sign-up',
};

const DEFAULT_CARDS = [
  {
    title: 'Just got NEXPLANON?',
    children: (
      <>
        <Button
          size="small"
          href="/insertion/#insertion-aftercare"
          target="_blank"
          rel="noopener"
          data-design-type="clickAction"
          data-design-category="button_vd"
          data-design-action="Button"
          data-design-label="Insertion Aftercare"
          data-design-nori_id="Inline Content"
          data-design-module_name="Cards"
          data-design-module_no="04"
        >
          Insertion Aftercare
        </Button>
      </>
    ),
  },
  {
    title: 'Looking for more info?',
    children: (
      <>
        <Button
          size="small"
          href="/what-to-expect/"
          target="_blank"
          rel="noopener"
          data-design-type="clickAction"
          data-design-category="button_vd"
          data-design-action="Button"
          data-design-label="What to Expect Guide"
          data-design-nori_id="Inline Content"
          data-design-module_name="Cards"
          data-design-module_no="04"
        >
          “What to Expect” Guide
        </Button>
      </>
    ),
  },
  {
    title: 'Additional questions?',
    children: (
      <>
        <Button
          size="small"
          href="/questions/"
          target="_blank"
          rel="noopener"
          data-design-type="clickAction"
          data-design-category="button_vd"
          data-design-action="Button"
          data-design-label="See FAQs"
          data-design-nori_id="Inline Content"
          data-design-module_name="Cards"
          data-design-module_no="04"
        >
          See FAQs
        </Button>
      </>
    ),
  },
];

const SELECTORS = {
  SUCCESS_SCREEN: '#gigya-subscribe-thank-you-screen',
  REGISTER_FORM: '#gigya-register-form',
};

export default function EnrollmentPage() {
  const router = useRouter();
  const scriptRef = useRef(null);
  const [formSent, setFormSent] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const { updateElementState } = useAppContext();

  const isGigyaReady = useGigyaSessionVerify((response) => {
    const isLoggedIn = response.errorCode === PATIENT_HUB_CONFIG.NO_ERROR_CODE;
    if (isLoggedIn) {
      router.push(PATIENT_HUB_CONFIG.DASHBOARD_URL);
    } else {
      window.gigya.accounts.showScreenSet(ENROLLMENT_SCREEN);
    }
  });

  const handleOpenLogin = createHandleOpenLogin();

  useEffect(() => {
    const scriptContainer = scriptRef.current;

    const mainObserver = new MutationObserver(function () {
      const form = scriptContainer.querySelector(SELECTORS.REGISTER_FORM);

      if (form && !form.dataset.listenerAdded) {
        form.dataset.listenerAdded = 'true';

        form.addEventListener('submit', function (e) {
          const emailInput =
            form.querySelector('input[name="email"]') ||
            form.querySelector('input[type="email"]');

          if (emailInput?.value) {
            setUserEmail(emailInput.value);
          }
        });
      }

      const successTarget = scriptContainer.querySelector(
        SELECTORS.SUCCESS_SCREEN,
      );

      if (successTarget) {
        setFormSent(true);
        updateElementState(UI_ELEMENTS.isLoggedIn, true);

        scriptContainer.scrollIntoView({ behavior: 'auto' });
        mainObserver.disconnect();
      }
    });

    mainObserver.observe(scriptContainer, {
      attributes: false,
      childList: true,
      subtree: true,
    });

    return () => {
      mainObserver.disconnect();
    };
  }, []);

  return (
    <Layout
      head={HEAD}
      showChatbox={false}
      basic
      loginButton
      showGigya={true}
      showFooterNav
    >
      <Hero
        image={HERO_BG_IMAGE}
        mobileImage={HERO_BG_IMAGE_MOBILE}
        className={styles.heroEnrollment}
      >
        <Container>
          <div className={styles.heroHeading}>
            <div className={styles.middleHeroText} data-qa-tag="middleHeroText">
              my <span>Nexplanon</span> patient hub
            </div>
          </div>
          <div className={styles.heroSubheading} data-qa-tag="heroSubheading">
            You've got your NEXPLANON inserted. What's next? Register with your
            insertion information to receive helpful tips and support throughout
            your journey.
          </div>
          <div className={styles.heroActions}>
            <Button
              data-design-type="clickAction"
              data-design-category="button_vd"
              data-design-action="Link-Anchor"
              data-design-label="Sign Up"
              data-design-nori_id="Hero"
              data-design-module_name="My NEXPLANON Patient Hub"
              data-design-module_no="01"
              href="#sign-up"
              data-qa-tag="button sign up"
            >
              Sign Up
            </Button>
            <Button
              type="secondary"
              data-design-type="clickAction"
              data-design-category="button_vd"
              data-design-action="Button"
              data-design-label="Log In"
              data-design-nori_id="Hero"
              data-design-module_name="My NEXPLANON Patient Hub"
              data-design-module_no="01"
              onClick={handleOpenLogin}
              disabled={!isGigyaReady}
              data-qa-tag="button log in"
            >
              Log In
            </Button>
          </div>
        </Container>
      </Hero>

      <div className={styles.wrapper}>
        <EnrollmentCards
          cards={ENROLLMENT_CARDS}
          title="Benefits of My NEXPLANON"
          data-design-visible="visible_vd"
          data-design-type="module"
          data-design-nori_id="Inline Content"
          data-design-module_name="Benefits of My NEXPLANON"
          data-design-module_no="02"
        />

        <div
          className={styles.scriptContainer}
          data-design-visible="visible_vd"
          data-design-type="module"
          data-design-nori_id="Form"
          data-design-module_name="Create your account"
          data-design-module_no="03"
          ref={scriptRef}
        >
          <div className={`${styles.scriptWrapper} columns`} id="sign-up" />
        </div>
        {formSent && (
          <div className={styles.mtCards}>
            <Button
              type="outlineSecondary2"
              data-design-type="clickAction"
              data-design-category="button_vd"
              data-design-action="Button"
              data-design-label="Go to My NEXPLANON"
              data-design-nori_id="Inline Content"
              data-design-module_name="Cards"
              data-design-module_no="04"
              href={PATIENT_HUB_CONFIG.DASHBOARD_URL}
              data-qa-tag="button Go to My NEXPLANON"
            >
              Go to My NEXPLANON
            </Button>
            <CardsBlock
              cards={DEFAULT_CARDS}
              data-design-visible="visible_vd"
              data-design-type="module"
              data-design-nori_id="Inline Content"
              data-design-module_name="Cards"
              data-design-module_no="04"
            />
          </div>
        )}
      </div>
    </Layout>
  );
}
